/**
 * useFileUpload — file upload / conversion / RVT-translation-polling subsystem for SitesPage.
 *
 * Extracted verbatim from src/components/SitesPage.tsx (upload state cluster + the
 * upload/conversion/polling handler band). Behavior-preserving: initial values, control
 * flow, polling start/clear conditions, unmount cleanup, and console/alert output are
 * byte-identical to the originals. Component-scope values the moved bodies read
 * (selectedAsset/assets, the upload-modal setters, getSelectedFile/setSelectedFile,
 * getLocalRvtFiles, refreshToken, loadAssets, and the two parent callbacks) arrive as
 * params. fileService / assetService / apiService are module imports, not params.
 *
 * NOTE (partial-by-design): the refresh-key state (refreshingRvtLevels /
 * setRvtLevelsRefreshing / refreshLocalRvtLevels) stays in SitesPage — it is shared with
 * the local-RVT and ACC refresh flows, so it is not part of the upload subsystem.
 * updateLocalRvtSetting / refreshLocalRvtLevels also stay (local-RVT settings, not upload).
 *
 * Hook placement: called at the moved band's original position in SitesPage (after all
 * of SitesPage's other effects and below every param declaration), so params are passed
 * by value — no var-hoisting thunk needed (unlike slice 1's loadAssets thunk, whose call
 * site sat above loadAssets' declaration). Hook-order stability: the 6 useState/useRef
 * relocate from before the ACC hook to this later slot (positional-stable — same class as
 * slice 1's refreshingRvtLevels wrinkle); the 3 useEffects were the only effects between
 * SitesPage's last staying effect and its return, so their execution order is preserved.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Asset, AssetFile } from '../../services/AssetService';
import { assetService } from '../../services/AssetService';
import { fileService, FileUploadProgress } from '../../services/FileService';
import { apiService } from '../../services/ApiService';
import { showErrorToast } from '../../components/shared/toast';

interface UseFileUploadParams {
  selectedAsset: Asset | null;
  setSelectedAsset: React.Dispatch<React.SetStateAction<Asset | null>>;
  assets: Asset[];
  setAssets: React.Dispatch<React.SetStateAction<Asset[]>>;
  uploadFileType: string;
  getSelectedFile: (assetId: string, fileType: string) => File | null;
  setSelectedFile: (assetId: string, fileType: string, file: File | null) => void;
  setShowUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowIFCUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowE57UploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowGEOTIFFUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPOIUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCompanionFiles: { [assetId: string]: File[] };
  setSelectedCompanionFiles: React.Dispatch<React.SetStateAction<{ [assetId: string]: File[] }>>;
  getLocalRvtFiles: (asset: Asset | null) => AssetFile[];
  refreshToken: () => Promise<void>;
  loadAssets: () => void | Promise<void>;
  onAssetUpdated?: (assetId: string) => void;
  onOpenAutodeskViewer?: (config: {
    urn: string;
    connectionId: string;
    assetId?: string;
    viewables: Array<{ guid: string; name: string; role: '3d' | '2d' }>;
    fileName?: string;
    defaultLevelName?: string | null;
    defaultLevelElevation?: number | null;
    initialViewableGuid?: string | null;
  }) => void;
}

export function useFileUpload({
  selectedAsset,
  setSelectedAsset,
  assets,
  setAssets,
  uploadFileType,
  getSelectedFile,
  setSelectedFile,
  setShowUploadModal,
  setShowIFCUploadModal,
  setShowE57UploadModal,
  setShowGEOTIFFUploadModal,
  setShowPOIUploadModal,
  selectedCompanionFiles,
  setSelectedCompanionFiles,
  getLocalRvtFiles,
  refreshToken,
  loadAssets,
  onAssetUpdated,
  onOpenAutodeskViewer,
}: UseFileUploadParams) {
  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: FileUploadProgress}>({});
  const [isUploading, setIsUploading] = useState(false);

  // IFC Conversion tracking
  const [ifcConversionStatus, setIfcConversionStatus] = useState<{
    [fileId: string]: {
      status: 'pending' | 'converting' | 'completed' | 'error';
      progress: number;
      message: string;
    }
  }>({});
  const [conversionPollingInterval, setConversionPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // RVT Translation tracking
  const [rvtTranslationStatus, setRvtTranslationStatus] = useState<{
    [fileId: string]: { status: string; progress: number }
  }>({});
  const rvtPollingRef = useRef<Record<string, NodeJS.Timeout>>({});

  // File upload
  const handleFileUpload = async () => {
    if (!selectedAsset) return;
    const file = getSelectedFile(selectedAsset.id, uploadFileType);
    if (!file) return;

    try {
      await refreshToken();
      await fileService.uploadFile(selectedAsset.id, file, uploadFileType);
      await loadAssets();

      const updatedAsset = assets.find(a => a.id === selectedAsset.id);
      if (updatedAsset) {
        setSelectedAsset(updatedAsset);
      }

      setShowUploadModal(false);
      setSelectedFile(selectedAsset.id, uploadFileType, null);

      // Notify parent component about the asset update
      onAssetUpdated?.(selectedAsset.id);
    } catch (error) {
      console.error('Failed to upload file:', error);
      showErrorToast('Failed to upload file. Please try again.');
    }
  };

  // Individual file upload handlers
  const handleSpecificFileUpload = async (fileType: string) => {
    if (!selectedAsset) {
      console.error('❌ UPLOAD BLOCKED: No selected asset');
      return;
    }

    let selectedFileType = fileType;
    let file = getSelectedFile(selectedAsset.id, fileType);

    // The model upload modal stores both IFC and RVT selections in the shared
    // model input slot. RVT is detected at upload time from the extension.
    if (!file && fileType === 'rvt') {
      const modelFile = getSelectedFile(selectedAsset.id, 'ifc');
      if (modelFile) {
        selectedFileType = 'ifc';
        file = modelFile;
      }
    }

    if (!file) {
      console.error('❌ UPLOAD BLOCKED: No file selected for', { fileType, assetId: selectedAsset.id });
      return;
    }


    try {
      setIsUploading(true);

      const companionFiles = selectedCompanionFiles[selectedAsset.id] || [];

      await refreshToken();
      const result = await fileService.uploadFile(
        selectedAsset.id,
        file,
        fileType,
        (progress: FileUploadProgress) => {
          setUploadProgress(prev => ({
            ...prev,
            [selectedAsset.id]: progress
          }));
        },
        companionFiles.length > 0 ? companionFiles : undefined
      );

      const freshAssets = await assetService.getAssets();
      setAssets(freshAssets);

      const updatedAsset = freshAssets.find(a => a.id === selectedAsset.id);
      if (updatedAsset) {
        setSelectedAsset(updatedAsset);
      }

      // Close appropriate modal and clear file selection
      setShowIFCUploadModal(false);
      setShowE57UploadModal(false);
      // setShowDWGUploadModal(false); // Commented out - using GEOTIFF instead
      setShowGEOTIFFUploadModal(false);
      setShowPOIUploadModal(false);
      setSelectedFile(selectedAsset.id, selectedFileType, null);
      if (selectedFileType !== fileType) {
        setSelectedFile(selectedAsset.id, fileType, null);
      }

      // Clear companion files
      setSelectedCompanionFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[selectedAsset.id];
        return newFiles;
      });

      // Start IFC conversion polling if this was an IFC upload
      if (fileType === 'ifc' && result?.file?.id) {
        startConversionPolling(selectedAsset.id, result.file.id);
      }

      // Start RVT translation polling if this was an RVT upload
      if (fileType === 'rvt' && result?.file?.id) {
        startRvtTranslationPolling(selectedAsset.id, result.file.id);
      }

      // Notify parent component about the asset update
      onAssetUpdated?.(selectedAsset.id);
    } catch (error) {
      console.error('❌ UPLOAD FAILED:', error);
      showErrorToast(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[selectedAsset.id];
        return newProgress;
      });
    }
  };

  // Handle file deletion with refresh
  const handleFileDelete = async (assetId: string, fileId: string, fileType: string) => {
    try {

      const success = await fileService.deleteFile(assetId, fileId);
      if (success) {
        const freshAssets = await assetService.getAssets();
        setAssets(freshAssets);

        // Update selected asset if it's currently selected
        const updatedAsset = freshAssets.find(a => a.id === assetId);
        if (updatedAsset && selectedAsset?.id === assetId) {
          setSelectedAsset(updatedAsset);
        }

        // Notify parent component about the asset update (MISSING!)
        onAssetUpdated?.(assetId);

      } else {
        showErrorToast('Failed to delete file. Please try again.');
      }
    } catch (error) {
      console.error('❌ DELETE FAILED:', error);
      showErrorToast(`Failed to delete file: ${error.message}`);
    }
  };

  // Check IFC fragment conversion status
  const checkIfcConversionStatus = async (assetId: string, fileId: string): Promise<boolean> => {
    try {
      const data = await apiService.get(`/assets/${assetId}/files/${fileId}/fragment-status`).catch(() => null);
      return data?.available === true;
    } catch (error) {
      console.error('Error checking IFC conversion status:', error);
      return false;
    }
  };

  // Poll for IFC conversion completion
  const startConversionPolling = (assetId: string, fileId: string) => {
    // Set initial conversion status
    setIfcConversionStatus(prev => ({
      ...prev,
      [fileId]: {
        status: 'converting',
        progress: 50,
        message: 'Converting IFC to optimized format...'
      }
    }));

    // Start polling
    const pollInterval = setInterval(async () => {
      const isComplete = await checkIfcConversionStatus(assetId, fileId);

      if (isComplete) {
        // Conversion complete
        setIfcConversionStatus(prev => ({
          ...prev,
          [fileId]: {
            status: 'completed',
            progress: 100,
            message: 'Conversion complete! Model optimized for fast loading.'
          }
        }));

        clearInterval(pollInterval);

        // Clear status after 3 seconds
        setTimeout(() => {
          setIfcConversionStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[fileId];
            return newStatus;
          });
        }, 3000);
      }
    }, 2000); // Poll every 2 seconds

    // Store interval for cleanup
    setConversionPollingInterval(pollInterval);

    // Stop polling after 10 minutes max
    setTimeout(() => {
      clearInterval(pollInterval);
      setIfcConversionStatus(prev => {
        if (prev[fileId]?.status === 'converting') {
          return {
            ...prev,
            [fileId]: {
              status: 'completed',
              progress: 100,
              message: 'Conversion may still be in progress for large files.'
            }
          };
        }
        return prev;
      });
    }, 600000);
  };

  // Cleanup conversion polling on unmount
  useEffect(() => {
    return () => {
      if (conversionPollingInterval) {
        clearInterval(conversionPollingInterval);
      }
    };
  }, [conversionPollingInterval]);

  // Check RVT translation status
  const checkRvtTranslationStatus = async (assetId: string, fileId: string): Promise<{ status: string; progress: number } | null> => {
    try {
      const data = await apiService.get(`/assets/${assetId}/files/${fileId}/rvt-status`).catch(() => null);
      if (!data) return null;
      return { status: data.status, progress: data.progress || 0 };
    } catch (error) {
      console.error('Error checking RVT translation status:', error);
      return null;
    }
  };

  // Poll for RVT translation completion
  const startRvtTranslationPolling = (assetId: string, fileId: string) => {
    // Keep one polling interval per file so multiple RVTs can translate safely.
    if (rvtPollingRef.current[fileId]) {
      return;
    }

    // Set initial status
    setRvtTranslationStatus(prev => ({
      ...prev,
      [fileId]: { status: 'translating', progress: 0 }
    }));

    const pollInterval = setInterval(async () => {
      const result = await checkRvtTranslationStatus(assetId, fileId);
      if (!result) return;

      setRvtTranslationStatus(prev => ({
        ...prev,
        [fileId]: result
      }));

      if (result.status === 'ready') {
        clearInterval(pollInterval);
        delete rvtPollingRef.current[fileId];

        // Set to 100% briefly
        setRvtTranslationStatus(prev => ({
          ...prev,
          [fileId]: { status: 'ready', progress: 100 }
        }));

        // Refresh assets to get updated metadata
        const freshAssets = await assetService.getAssets();
        setAssets(freshAssets);
        const updated = freshAssets.find(a => a.id === assetId);
        if (updated && selectedAsset?.id === assetId) {
          setSelectedAsset(updated);
        }

        // Clear status after 3 seconds
        setTimeout(() => {
          setRvtTranslationStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[fileId];
            return newStatus;
          });
        }, 3000);
      } else if (result.status === 'failed') {
        clearInterval(pollInterval);
        delete rvtPollingRef.current[fileId];

        // Refresh assets to show error
        const freshAssets = await assetService.getAssets();
        setAssets(freshAssets);
        const updated = freshAssets.find(a => a.id === assetId);
        if (updated && selectedAsset?.id === assetId) {
          setSelectedAsset(updated);
        }
      }
    }, 10000); // Poll every 10 seconds (RVT translation is slow)

    rvtPollingRef.current[fileId] = pollInterval;

    // Stop polling after 60 minutes max
    setTimeout(() => {
      if (rvtPollingRef.current[fileId] === pollInterval) {
        clearInterval(pollInterval);
        delete rvtPollingRef.current[fileId];
      }
    }, 3600000);
  };

  // Cleanup RVT polling on unmount
  useEffect(() => {
    return () => {
      Object.values(rvtPollingRef.current).forEach(clearInterval);
      rvtPollingRef.current = {};
    };
  }, []);

  // Auto-resume RVT polling if asset has translating files
  useEffect(() => {
    if (!selectedAsset) return;
    getLocalRvtFiles(selectedAsset)
      .filter(rvtFile => rvtFile?.metadata?.rvt_translation_status === 'translating')
      .forEach(rvtFile => startRvtTranslationPolling(selectedAsset.id, rvtFile.id));
    // Resume once per asset. The poller itself calls setAssets/setSelectedAsset, so
    // depending on the selectedAsset object would re-enter this on every poll tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset?.id]);

  // Local RVT helpers
  const handleOpenLocalRvt = (rvtFile: any) => {
    if (!selectedAsset) return;
    const metadata = rvtFile.metadata || {};
    const viewables = metadata.viewables || [];
    const levels = metadata.levels || [];
    const selectedLevel = metadata.rvt_default_level
      ? levels.find((l: any) => l.name === metadata.rvt_default_level)
      : null;

    onOpenAutodeskViewer?.({
      urn: metadata.viewable_urn,
      connectionId: 'local-rvt',
      assetId: selectedAsset.id,
      viewables: viewables.map((v: any) => ({ guid: v.guid, name: v.name, role: v.role })),
      fileName: rvtFile.fileName,
      defaultLevelName: metadata.rvt_default_level || null,
      defaultLevelElevation: selectedLevel?.elevation ?? null,
      initialViewableGuid: metadata.rvt_selected_viewable_guid || null,
    });
  };

  return {
    // state values read by SitesPage JSX
    uploadProgress,
    isUploading,
    ifcConversionStatus,
    rvtTranslationStatus,
    // handlers invoked by SitesPage JSX
    handleFileUpload,
    handleSpecificFileUpload,
    handleFileDelete,
    handleOpenLocalRvt,
  };
}
